import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { formatCurrency, formatDisplayAmount, toBaseCurrency, fromBaseCurrency, getCurrencyConfig, formatDate, API_URL } from "../lib/utils";
import { processedByText } from "../lib/fiscal";
import { TPASelector, useTPAs } from "../components/payments/TPASelector";
import { toastApiError } from "../lib/apiErrors";
import { printInvoice, mergeLines } from "../lib/invoicePrint";
import axios from "axios";
import { toast } from "sonner";
import {
  Clock,
  ChefHat,
  Check,
  X,
  RefreshCw,
  Receipt,
  Users,
  CreditCard,
  Banknote,
  Smartphone,
  Building2,
  Printer,
  Download,
  FileText,
  ClipboardList,
  Coins,
} from "lucide-react";
import { MixedPaymentEditor, emptyMixedParts, validateMixedParts, mixedPayload } from "../components/payments/MixedPaymentEditor";

const OrdersPage = () => {
  const { t } = useLanguage();
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  
  // Payment Modal
  const [paymentModal, setPaymentModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [tpaId, setTpaId] = useState("");
  const [mixedParts, setMixedParts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [creditCustomerId, setCreditCustomerId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerNif, setCustomerNif] = useState("");
  const [amountReceived, setAmountReceived] = useState("");
  const [changeToGive, setChangeToGive] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [giftCardCode, setGiftCardCode] = useState("");
  const [giftCardInfo, setGiftCardInfo] = useState(null);
  const [giftCardChecking, setGiftCardChecking] = useState(false);
  
  // Invoice Modal
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [settings, setSettings] = useState(null);
  
  const invoiceRef = useRef(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const tpas = useTPAs(headers);

  useEffect(() => {
    fetchOrders();
    fetchSettings();
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/orders`, {
        headers,
        withCredentials: true,
      });
      setOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`, {
        headers,
        withCredentials: true,
      });
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const r = await axios.put(
        `${API_URL}/orders/${orderId}/status?status=${status}`,
        {},
        { headers, withCredentials: true }
      );
      const restored = r.data?.stock_restored_items || [];
      toast.success(
        status === "cancelled" && restored.length
          ? `Pedido cancelado — stock reposto: ${restored.join(", ")}`
          : `Pedido atualizado para: ${getStatusLabel(status)}`
      );
      fetchOrders();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao atualizar pedido");
    }
  };

  // Calcular troco quando o valor recebido muda (input está na moeda do sistema)
  const handleAmountReceivedChange = (value) => {
    setAmountReceived(value);
    if (selectedOrder && value) {
      const received = toBaseCurrency(value);
      const total = selectedOrder.total || 0;
      const change = received - total;
      setChangeToGive(change > 0 ? change : 0);
    } else {
      setChangeToGive(0);
    }
  };

  // Botões de valores rápidos para pagamento (na moeda do sistema)
  const displayTotal = selectedOrder ? fromBaseCurrency(selectedOrder.total) : 0;
  const quickSteps = getCurrencyConfig().currency === "AOA" ? [100, 500, 1000, 5000] : [1, 5, 10, 50];
  const quickAmounts = selectedOrder ? quickSteps
    .map((s) => Math.ceil(displayTotal / s) * s)
    .filter((v, i, a) => a.indexOf(v) === i && v >= displayTotal).slice(0, 4) : [];

  const checkGiftCard = async () => {
    if (!giftCardCode) return;
    setGiftCardChecking(true);
    try {
      const res = await axios.get(`${API_URL}/gift-cards/lookup?code=${encodeURIComponent(giftCardCode.trim())}`, { headers, withCredentials: true });
      setGiftCardInfo(res.data);
      if (res.data.status !== "active") toast.error("Cartão não está ativo");
    } catch (err) {
      setGiftCardInfo(null);
      toast.error(err.response?.data?.detail || "Cartão não encontrado");
    } finally {
      setGiftCardChecking(false);
    }
  };

  const handlePayOrder = async () => {
    if (!selectedOrder) return;
    
    setProcessing(true);
    try {
      if (paymentMethod === "credit") {
        if (!creditCustomerId) {
          toast.error("Selecione o cliente para venda a crédito");
          setProcessing(false);
          return;
        }
        const response = await axios.put(
          `${API_URL}/orders/${selectedOrder.order_id}/credit?customer_id=${creditCustomerId}`,
          {},
          { headers, withCredentials: true }
        );
        toast.success(`Venda a crédito registada. Saldo do cliente: ${formatCurrency(response.data.customer_balance)}`);
        setPaymentModal(false);
        if (response.data.invoice) {
          setCurrentInvoice(response.data.invoice);
          setInvoiceModal(true);
        }
        fetchOrders();
        setSelectedOrder(null);
        setPaymentMethod("cash");
        setCreditCustomerId("");
        setProcessing(false);
        return;
      }
      if (paymentMethod === "giftcard" && !giftCardCode.trim()) {
        toast.error("Indique o código do cartão de consumo / gift card");
        setProcessing(false);
        return;
      }
      if (paymentMethod === "card" && tpas.length && !tpaId) {
        toast.error("Selecione o TPA onde o cartão foi passado");
        setProcessing(false);
        return;
      }
      const mixedErr = paymentMethod === "mixed" ? validateMixedParts(mixedParts, selectedOrder.total || 0, tpas) : null;
      if (mixedErr) {
        toast.error(mixedErr);
        setProcessing(false);
        return;
      }
      const params = new URLSearchParams({
        payment_method: paymentMethod,
      });
      if (customerName) params.append("customer_name", customerName);
      if (customerNif) params.append("customer_nif", customerNif);
      if (paymentMethod === "giftcard") params.append("gift_card_code", giftCardCode.trim());
      if (paymentMethod === "card" && tpaId) params.append("tpa_id", tpaId);
      
      // Adicionar valor recebido se for pagamento em dinheiro
      if (paymentMethod === "cash" && amountReceived) {
        params.append("amount_received", toBaseCurrency(amountReceived));
      }
      
      const response = await axios.put(
        `${API_URL}/orders/${selectedOrder.order_id}/pay?${params.toString()}`,
        paymentMethod === "mixed" ? mixedPayload(mixedParts) : {},
        { headers, withCredentials: true }
      );
      
      // Mostrar troco se existir
      if (response.data.change_given && response.data.change_given > 0) {
        toast.success(`Troco a devolver: ${formatCurrency(response.data.change_given)}`);
      } else if (response.data.gift_card_balance !== null && response.data.gift_card_balance !== undefined) {
        toast.success(`Pago com cartão. Saldo restante: ${formatCurrency(response.data.gift_card_balance)}`);
      } else {
        toast.success("Pedido pago com sucesso! Fatura gerada.");
      }
      
      setPaymentModal(false);
      
      // Mostrar fatura gerada
      if (response.data.invoice) {
        setCurrentInvoice(response.data.invoice);
        setInvoiceModal(true);
      }
      
      fetchOrders();
      
      // Reset form
      setSelectedOrder(null);
      setPaymentMethod("cash");
      setCustomerName("");
      setCustomerNif("");
      setAmountReceived("");
      setChangeToGive(0);
      setGiftCardCode("");
      setGiftCardInfo(null);
    } catch (error) {
      toastApiError(error, "Erro ao processar pagamento");
    } finally {
      setProcessing(false);
    }
  };

  const handleViewInvoice = async (orderId) => {
    try {
      const response = await axios.get(
        `${API_URL}/orders/${orderId}/invoice`,
        { headers, withCredentials: true }
      );
      setCurrentInvoice(response.data);
      setInvoiceModal(true);
    } catch (error) {
      toast.error("Fatura não encontrada");
    }
  };

  const handlePrintInvoice = () => {
    printInvoice(currentInvoice, settings || {});
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: "Novo",
      preparing: "Preparando",
      ready: "Pronto",
      served: "Servido",
      completed: "Pago",
      cancelled: "Cancelado",
    };
    return labels[status] || status;
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      open: "bg-emerald-500/20 text-emerald-500",
      preparing: "bg-amber-500/20 text-amber-500",
      ready: "bg-blue-500/20 text-blue-500",
      served: "bg-purple-500/20 text-purple-500",
      completed: "bg-slate-500/20 text-slate-500",
      cancelled: "bg-red-500/20 text-red-500",
    };
    return classes[status] || "";
  };

  const statusTabs = [
    { value: "all", label: t("all"), count: orders.length },
    { value: "open", label: "Novos", count: orders.filter((o) => o.status === "open").length },
    { value: "preparing", label: "Preparando", count: orders.filter((o) => o.status === "preparing").length },
    { value: "ready", label: "Prontos", count: orders.filter((o) => o.status === "ready").length },
    { value: "served", label: "Servidos", count: orders.filter((o) => o.status === "served").length },
    { value: "completed", label: "Pagos", count: orders.filter((o) => o.status === "completed").length },
  ];

  const filteredOrders = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">{t("orders")}</h1>
          <p className="text-muted-foreground">
            Gestão de pedidos - Pagamento gera fatura automática
          </p>
        </div>
        <Button variant="outline" onClick={fetchOrders} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="flex flex-wrap gap-1">
          {statusTabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="flex items-center gap-2"
            >
              {tab.label}
              <Badge variant="secondary" className="text-xs">
                {tab.count}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Orders Grid */}
      <ScrollArea className="h-[calc(100vh-280px)]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pr-4">
          {filteredOrders.map((order) => (
            <Card
              key={order.order_id}
              className="overflow-hidden"
              data-testid={`order-card-${order.order_id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-mono text-lg">
                    #{order.order_number}
                  </CardTitle>
                  <Badge className={getStatusBadgeClass(order.status)}>
                    {getStatusLabel(order.status)}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  {order.table_id && (
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      Mesa
                    </span>
                  )}
                  {order.order_type === "takeaway" && (
                    <Badge variant="outline">Takeaway</Badge>
                  )}
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDate(order.created_at)}
                  </span>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Items */}
                <div className="space-y-2">
                  {order.items?.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>
                        <span className="font-medium">{item.quantity}x</span>{" "}
                        {item.product_name}
                      </span>
                      <span className="font-mono">
                        {formatCurrency(item.quantity * item.unit_price)}
                      </span>
                    </div>
                  ))}
                  {order.items?.length > 3 && (
                    <p className="text-xs text-muted-foreground">
                      +{order.items.length - 3} mais itens
                    </p>
                  )}
                </div>

                {/* Total */}
                <div className="flex justify-between items-center pt-3 border-t">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold font-mono text-lg text-primary">
                    {formatCurrency(order.total)}
                  </span>
                </div>

                {/* Invoice info for completed orders */}
                {order.status === "completed" && order.invoice_number && (
                  <div className="p-2 rounded bg-muted/50 flex items-center justify-between">
                    <span className="text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      {order.invoice_number}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewInvoice(order.order_id)}
                      className="gap-1"
                    >
                      <Receipt className="w-3 h-3" />
                      Ver Fatura
                    </Button>
                  </div>
                )}

                {/* Actions */}
                {order.status !== "completed" && order.status !== "cancelled" && (
                  <div className="flex gap-2">
                    {order.status === "open" && (
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => updateOrderStatus(order.order_id, "preparing")}
                      >
                        <ChefHat className="w-4 h-4 mr-1" />
                        Preparar
                      </Button>
                    )}
                    {order.status === "preparing" && (
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => updateOrderStatus(order.order_id, "ready")}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Pronto
                      </Button>
                    )}
                    {order.status === "ready" && (
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => updateOrderStatus(order.order_id, "served")}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Servido
                      </Button>
                    )}
                    {order.status === "served" && (
                      <Button
                        size="sm"
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => {
                          setSelectedOrder(order);
                          setPaymentModal(true);
                        }}
                        data-testid={`order-pay-btn-${order.order_id}`}
                      >
                        <CreditCard className="w-4 h-4 mr-1" />
                        Pagar
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive"
                      onClick={() => updateOrderStatus(order.order_id, "cancelled")}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {order.status === "completed" && !order.invoice_number && (
                  <Badge variant="outline" className="w-full justify-center py-2">
                    <Check className="w-4 h-4 mr-2 text-emerald-500" />
                    Pago
                  </Badge>
                )}
              </CardContent>
            </Card>
          ))}

          {filteredOrders.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Receipt className="w-12 h-12 mb-4 opacity-50" />
              <p>Nenhum pedido encontrado</p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Payment Modal */}
      <Dialog open={paymentModal} onOpenChange={setPaymentModal}>
        <DialogContent data-testid="payment-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              Pagar Pedido #{selectedOrder?.order_number}
            </DialogTitle>
            <DialogDescription>
              Selecione o método de pagamento. A fatura será gerada automaticamente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Order Summary */}
            <Card>
              <CardContent className="p-4">
                <div className="space-y-2">
                  {mergeLines(selectedOrder?.items, { byNotes: true }).map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{item.quantity}x {item.product_name}</span>
                      <span className="font-mono">{formatCurrency(item.quantity * item.unit_price)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 border-t font-bold">
                    <span>Total a Pagar</span>
                    <span className="text-primary font-mono text-lg">
                      {formatCurrency(selectedOrder?.total || 0)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label>Método de Pagamento</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={paymentMethod === "cash" ? "default" : "outline"}
                  onClick={() => setPaymentMethod("cash")}
                  className="justify-start gap-2"
                >
                  <Banknote className="w-4 h-4" />
                  Numerário
                </Button>
                <Button
                  type="button"
                  variant={paymentMethod === "card" ? "default" : "outline"}
                  onClick={() => setPaymentMethod("card")}
                  className="justify-start gap-2"
                  data-testid="payment-method-card"
                >
                  <CreditCard className="w-4 h-4" />
                  Cartão
                </Button>
                <Button
                  type="button"
                  variant={paymentMethod === "transfer" ? "default" : "outline"}
                  onClick={() => setPaymentMethod("transfer")}
                  className="justify-start gap-2"
                >
                  <Building2 className="w-4 h-4" />
                  Transferência
                </Button>
                <Button
                  type="button"
                  variant={paymentMethod === "mobile" ? "default" : "outline"}
                  onClick={() => setPaymentMethod("mobile")}
                  className="justify-start gap-2"
                >
                  <Smartphone className="w-4 h-4" />
                  Multicaixa
                </Button>
                <Button
                  type="button"
                  variant={paymentMethod === "credit" ? "default" : "outline"}
                  onClick={async () => {
                    setPaymentMethod("credit");
                    if (customers.length === 0) {
                      try {
                        const res = await axios.get(`${API_URL}/customers`, { headers, withCredentials: true });
                        setCustomers(res.data);
                      } catch { /* ignore */ }
                    }
                  }}
                  className="justify-start gap-2 col-span-2"
                  data-testid="credit-payment-btn"
                >
                  <ClipboardList className="w-4 h-4" />
                  Venda a Crédito (Conta Corrente)
                </Button>
                <Button
                  type="button"
                  variant={paymentMethod === "giftcard" ? "default" : "outline"}
                  onClick={() => setPaymentMethod("giftcard")}
                  className="justify-start gap-2 col-span-2"
                  data-testid="giftcard-payment-btn"
                >
                  <CreditCard className="w-4 h-4" />
                  Cartão de Consumo / Gift Card
                </Button>
                <Button
                  type="button"
                  variant={paymentMethod === "mixed" ? "default" : "outline"}
                  onClick={() => { setPaymentMethod("mixed"); setMixedParts(emptyMixedParts(selectedOrder?.total || 0, tpas)); }}
                  className="justify-start gap-2 col-span-2"
                  data-testid="mixed-payment-btn"
                >
                  <Coins className="w-4 h-4" />
                  Pagamento Misto (Numerário + TPA + …)
                </Button>
              </div>
            </div>

            {paymentMethod === "card" && <TPASelector tpas={tpas} value={tpaId} onChange={setTpaId} />}
            {paymentMethod === "mixed" && mixedParts.length > 0 && <MixedPaymentEditor parts={mixedParts} onChange={setMixedParts} total={selectedOrder?.total || 0} tpas={tpas} />}

            {paymentMethod === "giftcard" && (
              <div className="space-y-2 p-3 rounded-lg border bg-primary/5" data-testid="giftcard-payment-section">
                <Label>Código do Cartão *</Label>
                <div className="flex gap-2">
                  <Input
                    value={giftCardCode}
                    onChange={(e) => { setGiftCardCode(e.target.value.toUpperCase()); setGiftCardInfo(null); }}
                    placeholder="LUM-XXXX-XXXX"
                    className="font-mono"
                    data-testid="giftcard-code-input"
                  />
                  <Button type="button" variant="outline" disabled={!giftCardCode || giftCardChecking} onClick={checkGiftCard} data-testid="giftcard-check-btn">
                    {giftCardChecking ? "..." : "Verificar"}
                  </Button>
                </div>
                {giftCardInfo && (
                  <div className={`p-2 rounded-md text-sm ${giftCardInfo.balance >= (selectedOrder?.total || 0) ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-red-500/10 text-red-600"}`} data-testid="giftcard-balance-info">
                    Saldo: <strong className="font-mono">{formatCurrency(giftCardInfo.balance)}</strong>
                    {giftCardInfo.balance < (selectedOrder?.total || 0) && " — saldo insuficiente para este pedido"}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">O total do pedido será debitado do saldo do cartão.</p>
              </div>
            )}

            {paymentMethod === "credit" && (
              <div className="space-y-2 p-3 rounded-lg border bg-amber-500/5">
                <Label>Cliente da Conta Corrente *</Label>
                <Select value={creditCustomerId} onValueChange={setCreditCustomerId}>
                  <SelectTrigger data-testid="credit-customer-select">
                    <SelectValue placeholder="Selecionar cliente registado" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.customer_id} value={c.customer_id}>
                        {c.name}{c.nif ? ` (NIF ${c.nif})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  O valor fica em dívida na conta corrente do cliente. Se não existir, crie o cliente na página Clientes.
                </p>
              </div>
            )}

            {/* Customer Info (Optional) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome do Cliente (opcional)</Label>
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Consumidor Final"
                />
              </div>
              <div className="space-y-2">
                <Label>NIF (opcional)</Label>
                <Input
                  value={customerNif}
                  onChange={(e) => setCustomerNif(e.target.value)}
                  placeholder="999999999"
                />
              </div>
            </div>

            {/* Cash Change Calculator - apenas para pagamento em dinheiro */}
            {paymentMethod === "cash" && (
              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Banknote className="w-4 h-4" />
                      Cálculo de Troco
                    </Label>
                    <span className="text-sm text-muted-foreground">Total: {formatCurrency(selectedOrder?.total || 0)}</span>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Valor Recebido</Label>
                    <Input
                      type="number"
                      value={amountReceived}
                      onChange={(e) => handleAmountReceivedChange(e.target.value)}
                      placeholder={`Mínimo: ${formatCurrency(selectedOrder?.total || 0)}`}
                      className="text-lg font-mono"
                    />
                  </div>

                  {/* Quick Amount Buttons */}
                  {quickAmounts.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {quickAmounts.map((amount) => (
                        <Button
                          key={amount}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleAmountReceivedChange(amount.toString())}
                          className="font-mono"
                        >
                          {formatDisplayAmount(amount)}
                        </Button>
                      ))}
                    </div>
                  )}

                  {/* Change Display */}
                  {changeToGive > 0 && (
                    <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                          Troco a Devolver:
                        </span>
                        <span className="text-2xl font-bold font-mono text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(changeToGive)}
                        </span>
                      </div>
                    </div>
                  )}

                  {parseFloat(amountReceived) > 0 && toBaseCurrency(amountReceived) < (selectedOrder?.total || 0) && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-red-700 dark:text-red-400">
                          Falta:
                        </span>
                        <span className="text-xl font-bold font-mono text-red-600 dark:text-red-400">
                          {formatCurrency((selectedOrder?.total || 0) - toBaseCurrency(amountReceived))}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setPaymentModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handlePayOrder}
              disabled={processing}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-payment-btn"
            >
              {processing ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Confirmar Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Modal */}
      <Dialog open={invoiceModal} onOpenChange={setInvoiceModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="invoice-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Fatura {currentInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>

          {/* Printable Invoice Content */}
          <div ref={invoiceRef} className="space-y-4 p-4 border rounded-lg bg-white text-black">
            {/* Header with Logo */}
            <div className="text-center border-b pb-4">
              {/* Logo */}
              {settings?.logo_url && (
                <div className="flex justify-center mb-3">
                  <img
                    src={settings.logo_url.startsWith("/api") ? `${API_URL.replace("/api", "")}${settings.logo_url}` : settings.logo_url}
                    alt="Logo"
                    className="max-h-16 max-w-[150px] object-contain"
                  />
                </div>
              )}
              <p className="text-lg font-bold">{settings?.restaurant_name || "Restaurante"}</p>
              <p className="text-xs text-gray-600">NIF: {settings?.nif || "000000000"}</p>
              <p className="text-xs text-gray-600">{settings?.address || "Angola"}</p>
              <p className="text-xs text-gray-600">Tel: {settings?.phone || ""}</p>
            </div>

            {/* Invoice Info */}
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-600">Documento:</span>
                <span className="font-mono font-bold">{currentInvoice?.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Data:</span>
                <span>{currentInvoice?.created_at ? formatDate(currentInvoice.created_at) : ""}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Cliente:</span>
                <span>{currentInvoice?.customer_name || "Consumidor Final"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">NIF:</span>
                <span>{currentInvoice?.customer_nif || "999999999"}</span>
              </div>
            </div>

            {/* Items */}
            <div className="border-t border-b py-3 space-y-2">
              {mergeLines(currentInvoice?.items).map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <div className="flex-1">
                    <span className="font-medium">{item.quantity}x</span> {item.product_name}
                    {item.tax_rate !== undefined && (
                      <span className="text-xs text-gray-500 ml-1">
                        ({item.tax_code || "NOR"} {item.tax_rate}%)
                      </span>
                    )}
                  </div>
                  <span className="font-mono">{formatCurrency(item.line_total || (item.quantity * item.unit_price))}</span>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Subtotal (Líquido):</span>
                <span className="font-mono">{formatCurrency(currentInvoice?.subtotal || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span>IVA:</span>
                <span className="font-mono">{formatCurrency(currentInvoice?.tax || 0)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t">
                <span>TOTAL:</span>
                <span className="font-mono">{formatCurrency(currentInvoice?.total || 0)}</span>
              </div>
            </div>

            {/* Payment & Change Info */}
            {(currentInvoice?.payment_method_label || currentInvoice?.tpa_name) && (
              <div className="text-sm pt-2 border-t" data-testid="orders-invoice-payment">
                <span className="text-muted-foreground">Pagamento: </span>
                <span className="font-medium">{currentInvoice.payment_method_label || "Multicaixa"}{currentInvoice.tpa_name && !(currentInvoice.payment_method_label || "").includes(currentInvoice.tpa_name) ? ` · ${currentInvoice.tpa_name}` : ""}</span>
              </div>
            )}
            {currentInvoice?.amount_received && (
              <div className="text-sm space-y-1 pt-2 border-t bg-gray-50 p-2 rounded">
                <div className="flex justify-between">
                  <span className="text-gray-600">Valor Recebido:</span>
                  <span className="font-mono">{formatCurrency(currentInvoice.amount_received)}</span>
                </div>
                {currentInvoice?.change_given > 0 && (
                  <div className="flex justify-between font-bold text-emerald-600">
                    <span>Troco:</span>
                    <span className="font-mono">{formatCurrency(currentInvoice.change_given)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Bank Details */}
            {settings?.show_bank_details_on_invoice && settings?.bank_iban && (
              <div className="text-xs text-gray-600 pt-2 border-t space-y-1">
                <p className="font-semibold text-gray-700">Dados Bancários:</p>
                <p>{settings.bank_name} - {settings.bank_account_holder}</p>
                <p>IBAN: {settings.bank_iban}</p>
                {settings.bank_bic && <p>BIC/SWIFT: {settings.bank_bic}</p>}
              </div>
            )}

            {/* QR Code */}
            {settings?.show_qr_code_on_invoice && currentInvoice?.qr_code_data && (
              <div className="flex flex-col items-center pt-3 border-t">
                <p className="text-xs text-gray-500 mb-2">Código QR para verificação AGT</p>
                <img
                  src={currentInvoice.qr_code_data}
                  alt="QR Code Fatura"
                  className="w-24 h-24"
                />
              </div>
            )}

            {/* Menção legal (Decreto 312/18) */}
            <div className="text-xs text-gray-500 pt-3 border-t space-y-1">
              <p className="text-center font-mono break-all">{currentInvoice ? processedByText(currentInvoice) : ""}</p>
              <p className="text-center">Documento processado por computador</p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setInvoiceModal(false)}>
              Fechar
            </Button>
            <Button variant="outline" onClick={handlePrintInvoice} className="gap-2">
              <Printer className="w-4 h-4" />
              Imprimir
            </Button>
            <Button onClick={handlePrintInvoice} className="gap-2">
              <Download className="w-4 h-4" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
